
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

interface Author {
  id: string;
  display_name: string;
  avatar_url?: string;
  bio?: string;
  linkedin_url?: string;
}

interface AuthorSelectorProps {
  currentAuthorId?: string;
  currentAuthorName?: string;
  onAuthorChange: (authorId: string, authorName: string, authorAvatar?: string, authorBio?: string, authorLinkedIn?: string) => void;
  disabled?: boolean;
}

export const AuthorSelector: React.FC<AuthorSelectorProps> = ({
  currentAuthorId,
  currentAuthorName,
  onAuthorChange,
  disabled = false
}) => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuthorId, setSelectedAuthorId] = useState<string>(currentAuthorId || '');

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .order('display_name', { ascending: true });
          
        if (error) throw error;
        
        setAuthors(data as Author[]);
        
        // If we have a currentAuthorName but no currentAuthorId, try to find the author
        if (!currentAuthorId && currentAuthorName) {
          const foundAuthor = data.find(author => 
            author.display_name.toLowerCase() === currentAuthorName.toLowerCase()
          );
          
          if (foundAuthor) {
            setSelectedAuthorId(foundAuthor.id);
            onAuthorChange(
              foundAuthor.id, 
              foundAuthor.display_name, 
              foundAuthor.avatar_url,
              foundAuthor.bio,
              foundAuthor.linkedin_url
            );
          }
        }
      } catch (error) {
        console.error('Error fetching authors:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAuthors();
  }, [currentAuthorId, currentAuthorName, onAuthorChange]);

  const handleAuthorChange = (value: string) => {
    setSelectedAuthorId(value);
    
    if (value) {
      const selectedAuthor = authors.find(author => author.id === value);
      if (selectedAuthor) {
        onAuthorChange(
          selectedAuthor.id, 
          selectedAuthor.display_name, 
          selectedAuthor.avatar_url,
          selectedAuthor.bio,
          selectedAuthor.linkedin_url
        );
      }
    } else {
      // Default to edgefi team
      onAuthorChange('', 'edgefi team');
    }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <Label className="block font-medium">Author</Label>
        
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full rounded-md" />
            <div className="flex items-center space-x-4 mt-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Select
              value={selectedAuthorId || ''}
              onValueChange={handleAuthorChange}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">edgefi team</SelectItem>
                {authors.map((author) => (
                  <SelectItem key={author.id} value={author.id}>
                    {author.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedAuthorId && (
              <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-md">
                {(() => {
                  const selectedAuthor = authors.find(a => a.id === selectedAuthorId);
                  return (
                    <>
                      <Avatar className="h-12 w-12 border border-gray-200">
                        {selectedAuthor?.avatar_url ? (
                          <img 
                            src={selectedAuthor.avatar_url} 
                            alt={selectedAuthor.display_name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full bg-edgefi-amethyst/20 text-edgefi-purple flex items-center justify-center text-lg font-bold">
                            {selectedAuthor?.display_name[0] || ''}
                          </div>
                        )}
                      </Avatar>
                      <div>
                        <p className="font-medium">{selectedAuthor?.display_name}</p>
                        {selectedAuthor?.bio && (
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {selectedAuthor.bio}
                          </p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
